// Shopify CRO Frontend Service
// API calls to backend for Shopify CRO functionality

const API_BASE = 'http://localhost:3001/api/shopify';

/**
 * Get Shopify connections for a brand
 */
export async function getShopifyConnections(userId, brandId) {
    const response = await fetch(
        `${API_BASE}/connections?userId=${userId}&brandId=${brandId}`
    );
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch connections');
    }
    return data.connections;
}

/**
 * Disconnect a Shopify store
 */
export async function disconnectShopify(connectionId, userId) {
    const response = await fetch(`${API_BASE}/disconnect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId, userId })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to disconnect');
    }
    return data;
}

/**
 * Get store overview info
 */
export async function getStoreInfo(connectionId, userId) {
    const response = await fetch(
        `${API_BASE}/store-info?connectionId=${connectionId}&userId=${userId}`
    );
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch store info');
    }
    return data;
}

/**
 * Get products list
 */
export async function getProducts(connectionId, userId, limit = 50) {
    const response = await fetch(
        `${API_BASE}/products?connectionId=${connectionId}&userId=${userId}&limit=${limit}`
    );
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch products');
    }
    return data.products;
}

/**
 * Get collections list
 */
export async function getCollections(connectionId, userId) {
    const response = await fetch(
        `${API_BASE}/collections?connectionId=${connectionId}&userId=${userId}`
    );
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch collections');
    }
    return data.collections;
}

/**
 * Get analytics summary
 */
export async function getAnalytics(connectionId, userId, startDate, endDate) {
    let url = `${API_BASE}/analytics?connectionId=${connectionId}&userId=${userId}`;
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;

    const response = await fetch(url);
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch analytics');
    }
    return data;
}

/**
 * Analyze a product for CRO
 */
export async function analyzeProduct(connectionId, userId, productId) {
    const response = await fetch(`${API_BASE}/analyze/product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId, userId, productId })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to analyze product');
    }
    return data;
}

/**
 * Analyze a collection for CRO
 */
export async function analyzeCollection(connectionId, userId, collectionId) {
    const response = await fetch(`${API_BASE}/analyze/collection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId, userId, collectionId })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to analyze collection');
    }
    return data;
}

/**
 * Get full CRO analysis
 */
export async function getFullCROAnalysis(connectionId, userId) {
    const response = await fetch(`${API_BASE}/analyze/full`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId, userId })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to get CRO analysis');
    }
    return data;
}

/**
 * Apply product changes
 */
export async function applyProductChanges(connectionId, userId, productId, changes) {
    const response = await fetch(`${API_BASE}/apply/product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId, userId, productId, changes })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to apply changes');
    }
    return data;
}

// ===== A/B Testing =====

/**
 * Get all A/B tests
 */
export async function getABTests(connectionId, userId) {
    const response = await fetch(
        `${API_BASE}/ab-tests?connectionId=${connectionId}&userId=${userId}`
    );
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to fetch A/B tests');
    }
    return data.tests;
}

/**
 * Create a new A/B test
 */
export async function createABTest(connectionId, userId, testConfig) {
    const response = await fetch(`${API_BASE}/ab-tests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ connectionId, userId, ...testConfig })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to create A/B test');
    }
    return data;
}

/**
 * Start an A/B test
 */
export async function startABTest(testId, userId) {
    const response = await fetch(`${API_BASE}/ab-tests/${testId}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to start test');
    }
    return data;
}

/**
 * Pause an A/B test
 */
export async function pauseABTest(testId, userId) {
    const response = await fetch(`${API_BASE}/ab-tests/${testId}/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to pause test');
    }
    return data;
}

/**
 * Stop an A/B test and get results
 */
export async function stopABTest(testId, userId) {
    const response = await fetch(`${API_BASE}/ab-tests/${testId}/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to stop test');
    }
    return data;
}

/**
 * Apply winning variation permanently
 */
export async function applyWinningVariation(testId, userId, variationId) {
    const response = await fetch(`${API_BASE}/ab-tests/${testId}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId, variationId })
    });
    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.error || 'Failed to apply variation');
    }
    return data;
}

export default {
    getShopifyConnections,
    disconnectShopify,
    getStoreInfo,
    getProducts,
    getCollections,
    getAnalytics,
    analyzeProduct,
    analyzeCollection,
    getFullCROAnalysis,
    applyProductChanges,
    getABTests,
    createABTest,
    startABTest,
    pauseABTest,
    stopABTest,
    applyWinningVariation
};
